import type {
  AuthUser,
  ForgotPasswordRequest,
  LoginCredentials,
  LoginResponseBody,
  PasswordResetCompleteResponse,
  PasswordSetupRequest,
  SessionResponseBody,
  TokenVerifyResponse,
} from "./types";
import { useAuthStore } from "./store";

const SESSION_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

function authApiRoot(): string {
  const base = import.meta.env.VITE_API_URL?.trim().replace(/\/+$/, "") ?? "";
  if (base) {
    return `${base}/api/auth`;
  }
  return "/api/auth";
}

const AUTH_API = authApiRoot();
let authTransitionDepth = 0;
const activeRefreshPromises = new Set<Promise<SessionResponseBody | null>>();

function authHeaders(): HeadersInit | undefined {
  const token = useAuthStore.getState().token;
  return authHeadersForToken(token);
}

function authHeadersForToken(token: string | null): HeadersInit | undefined {
  if (!token) {
    return undefined;
  }
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function runAuthTransition<T>(fn: () => Promise<T>): Promise<T> {
  authTransitionDepth += 1;
  try {
    while (activeRefreshPromises.size > 0) {
      await Promise.allSettled(Array.from(activeRefreshPromises));
    }
    return await fn();
  } finally {
    authTransitionDepth = Math.max(0, authTransitionDepth - 1);
  }
}

function currentSessionSnapshot(): SessionResponseBody | null {
  const { user, token, impersonation } = useAuthStore.getState();
  if (!user || !token) {
    return null;
  }
  return {
    user,
    access_token: token,
    impersonation,
  };
}

async function parseDetail(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown };
    if (typeof data.detail === "string") {
      return data.detail;
    }
    if (Array.isArray(data.detail)) {
      const message = data.detail
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const msg = "msg" in item && typeof item.msg === "string" ? item.msg : null;
          return msg;
        })
        .filter((value): value is string => Boolean(value))
        .join(". ");
      if (message) {
        return message;
      }
    }
  } catch {
    /* empty */
  }
  return `Request failed (${res.status})`;
}

/**
 * Sign in via FastAPI `/api/auth/login` — sets httpOnly cookie and returns a bearer token for API calls.
 */
export async function login(credentials: LoginCredentials): Promise<LoginResponseBody> {
  const res = await fetch(`${AUTH_API}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(credentials),
  });

  if (!res.ok) {
    throw new Error(await parseDetail(res));
  }

  return (await res.json()) as LoginResponseBody;
}

/** Clear httpOnly session cookie (client store cleared separately or via `logout()` helper). */
export async function logoutApi(): Promise<void> {
  const res = await fetch(`${AUTH_API}/logout`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`Sign out failed (${res.status})`);
  }
}

/** Extend cookie lifetime; swap for refresh-token grant when backend supports it. */
export async function refresh(): Promise<SessionResponseBody | null> {
  if (authTransitionDepth > 0) {
    return currentSessionSnapshot();
  }

  const stateAtStart = useAuthStore.getState();
  const tokenAtStart = stateAtStart.token;
  const userIdAtStart = stateAtStart.user?.id ?? null;

  const promise = (async () => {
    const res = await fetch(`${AUTH_API}/refresh`, {
      method: "POST",
      headers: authHeadersForToken(tokenAtStart),
      credentials: "include",
      cache: "no-store",
    });

    if (!res.ok) {
      const currentState = useAuthStore.getState();
      const sameSession =
        currentState.token === tokenAtStart &&
        (currentState.user?.id ?? null) === userIdAtStart;
      if ((res.status === 401 || res.status === 403) && sameSession) {
        currentState.clearSession();
      }
      return null;
    }

    const body = (await res.json()) as SessionResponseBody;
    const currentState = useAuthStore.getState();
    if (
      authTransitionDepth > 0 ||
      currentState.token !== tokenAtStart ||
      (currentState.user?.id ?? null) !== userIdAtStart
    ) {
      return currentSessionSnapshot();
    }
    currentState.setSession({ user: body.user, token: body.access_token, impersonation: body.impersonation ?? null });
    return body;
  })();

  activeRefreshPromises.add(promise);
  try {
    return await promise;
  } finally {
    activeRefreshPromises.delete(promise);
  }
}

export function getSessionRefreshIntervalMs(): number {
  return SESSION_REFRESH_INTERVAL_MS;
}

/**
 * Load the current user from the httpOnly session.
 * On failure, clears the client store (cookie + bearer fallback stay in sync).
 * Never throws — avoids an infinite "Loading workspace…" if the fetch errors or times out.
 *
 * If `externalSignal` aborts (e.g. React effect cleanup), returns null **without** clearing the store
 * so a late `/api/auth/session` response cannot wipe state after Zustand persist rehydration.
 */
export async function getCurrentUser(externalSignal?: AbortSignal): Promise<AuthUser | null> {
  const composite = new AbortController();
  const timeoutId = setTimeout(() => composite.abort(), 20_000);

  const onExternalAbort = () => composite.abort();
  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      return null;
    }
    externalSignal.addEventListener("abort", onExternalAbort, { once: true });
  }

  try {
    const res = await fetch(`${AUTH_API}/session`, {
      method: "GET",
      headers: authHeaders(),
      credentials: "include",
      cache: "no-store",
      signal: composite.signal,
    });
    clearTimeout(timeoutId);

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        useAuthStore.getState().clearSession();
        return null;
      }
      throw new Error(await parseDetail(res));
    }

    const body = (await res.json()) as SessionResponseBody;
    useAuthStore.getState().setSession({ user: body.user, token: body.access_token, impersonation: body.impersonation ?? null });
    return body.user;
  } catch {
    clearTimeout(timeoutId);
    if (externalSignal?.aborted) {
      return null;
    }
    if (composite.signal.aborted) {
      throw new Error("Session request timed out");
    }
    throw new Error("Unable to verify session");
  } finally {
    if (externalSignal) {
      externalSignal.removeEventListener("abort", onExternalAbort);
    }
  }
}

/** Login and persist session (cookie + zustand including localStorage fallbacks). */
export async function loginAndPersist(credentials: LoginCredentials): Promise<AuthUser> {
  const { user, access_token } = await login(credentials);
  useAuthStore.getState().setSession({ user, token: access_token, impersonation: null });
  return user;
}

/** Revoke cookie and wipe client session. */
export async function logout(): Promise<void> {
  try {
    await logoutApi();
  } catch {
    /* Cookie may already be gone; still clear client state. */
  }
  useAuthStore.getState().clearSession();
}

export async function impersonateUser(userId: string): Promise<AuthUser> {
  return runAuthTransition(async () => {
    const res = await fetch(`${AUTH_API}/impersonate/user/${encodeURIComponent(userId)}`, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error(await parseDetail(res));
    }

    const body = (await res.json()) as SessionResponseBody;
    useAuthStore.getState().setSession({ user: body.user, token: body.access_token, impersonation: body.impersonation ?? null });
    return body.user;
  });
}

export async function exitImpersonation(): Promise<AuthUser> {
  return runAuthTransition(async () => {
    const res = await fetch(`${AUTH_API}/impersonate/exit`, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error(await parseDetail(res));
    }

    const body = (await res.json()) as SessionResponseBody;
    useAuthStore.getState().setSession({ user: body.user, token: body.access_token, impersonation: body.impersonation ?? null });
    return body.user;
  });
}

export async function forgotPassword(body: ForgotPasswordRequest): Promise<void> {
  const res = await fetch(`${AUTH_API}/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(await parseDetail(res));
  }
}

export async function verifyInvitationToken(token: string): Promise<TokenVerifyResponse> {
  const res = await fetch(`${AUTH_API}/invitation/verify?token=${encodeURIComponent(token)}`, {
    method: "GET",
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error(await parseDetail(res));
  }

  return (await res.json()) as TokenVerifyResponse;
}

export async function acceptInvitation(body: PasswordSetupRequest): Promise<LoginResponseBody> {
  const res = await fetch(`${AUTH_API}/invitation/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(await parseDetail(res));
  }

  return (await res.json()) as LoginResponseBody;
}

export async function verifyPasswordResetToken(token: string): Promise<TokenVerifyResponse> {
  const res = await fetch(`${AUTH_API}/password-reset/verify?token=${encodeURIComponent(token)}`, {
    method: "GET",
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error(await parseDetail(res));
  }

  return (await res.json()) as TokenVerifyResponse;
}

export async function completePasswordReset(body: PasswordSetupRequest): Promise<PasswordResetCompleteResponse> {
  const res = await fetch(`${AUTH_API}/password-reset/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(await parseDetail(res));
  }

  return (await res.json()) as PasswordResetCompleteResponse;
}
